/**
 * TemperatureChartBase - shared logic for Style01/02
 * Range dropdown + sensor switch (from chartableSensorConfig)
 * When no deviceSN: use mock. When deviceSN: fetch from API.
 */
import { useState, useMemo, useEffect } from 'react'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import {
  getChartableSensors,
  CHARTABLE_SENSOR_CONFIG,
  type ChartableSensorId,
  type ChartableSensorOption,
} from './chartableSensorConfig'
import {
  querySensorReportWithAuthContext,
  type GenerateReportDeviceData,
} from '../../services/turingApi'
import './TemperatureChart.css'

export type TimeRange = '24H' | '1W'

const TIME_RANGES: { id: TimeRange; label: string }[] = [
  { id: '24H', label: '24H' },
  { id: '1W', label: '1 Week' },
]

const TEMPERATURE_SENSOR_IDS: ChartableSensorId[] = ['temperature', 'humidity']
const TEMPERATURE_SENSOR_OPTIONS: ChartableSensorOption[] = TEMPERATURE_SENSOR_IDS.map((id) => CHARTABLE_SENSOR_CONFIG[id])

function filterTemperatureSensors(options: ChartableSensorOption[]): ChartableSensorOption[] {
  const allowed = options.filter((option) => TEMPERATURE_SENSOR_IDS.includes(option.id))
  const sorted = allowed.sort(
    (a, b) => TEMPERATURE_SENSOR_IDS.indexOf(a.id) - TEMPERATURE_SENSOR_IDS.indexOf(b.id)
  )
  return sorted
}

function formatTimeLabel(
  timestamp: number,
  range: TimeRange
): string {
  const t = new Date(timestamp)
  if (range === '1W') {
    return t.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' })
  }
  return t.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
}

function generateMockData(
  range: TimeRange,
  sensorId: ChartableSensorId
): Array<{ time: number; value: number }> {
  const cfg = CHARTABLE_SENSOR_CONFIG[sensorId]
  if (!cfg) return []

  const now = Date.now()
  const points = range === '24H' ? 24 : 7
  const intervalMs =
    range === '24H'
      ? 60 * 60 * 1000
      : 24 * 60 * 60 * 1000

  const data: Array<{ time: number; value: number }> = []
  let base = cfg.base
  for (let i = points; i >= 0; i--) {
    const t = new Date(now - i * intervalMs)
    base += (Math.random() - 0.5) * 2
    base = Math.max(cfg.min, Math.min(cfg.max, base))
    const value = Math.round(base * 10) / 10
    data.push({ time: t.getTime(), value })
  }
  return data
}

function mapTimeRangeToGenerateReport(range: TimeRange): {
  duration: string;
  granularity: 'hour' | 'day';
} {
  if (range === '24H') return { duration: '24h', granularity: 'hour' }
  return { duration: '7d', granularity: 'day' }
}

function toTimestamp(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value !== 'string') return null
  const ms = new Date(value).getTime()
  return Number.isFinite(ms) ? ms : null
}

function toNumber(value: unknown): number | null {
  if (Array.isArray(value)) {
    if (value.length === 0) return null
    return toNumber(value[0])
  }
  if (value && typeof value === 'object') {
    const rec = value as Record<string, unknown>
    if ('value' in rec) return toNumber(rec.value)
  }
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const num = Number(value)
    return Number.isFinite(num) ? num : null
  }
  return null
}

function resolveSensorValue(point: Record<string, unknown>, sensor: ChartableSensorId): number | null {
  const preferredKeys: Record<ChartableSensorId, string[]> = {
    temperature: [
      'temperatureAmbientCelsius',
      'temperature_ambient_celsius',
      'thermostatTemperatureAmbient',
      'ambientTemperatureCelsius',
      'temperature',
      'temp',
      'avgTemperature',
      'currentTemperature',
    ],
    humidity: [
      'humidityAmbientPercent',
      'humidity_ambient_percent',
      'relativeHumidity',
      'ambientHumidityPercent',
      'humidity',
      'rh',
      'avgHumidity',
      'currentHumidity',
    ],
    watt: ['watt', 'avgPower'],
    watt_hour: ['watt_hour', 'energy'],
    voltage: ['voltage', 'avgVoltage'],
    lux: ['lux', 'illuminance', 'avgIlluminance'],
    pm25: ['pm25', 'avgPm25'],
    pm10: ['pm10', 'avgPm10'],
    aqi: ['aqi', 'avgAqi'],
    co2: ['co2', 'avgCo2'],
  }

  for (const key of preferredKeys[sensor] || [sensor]) {
    const num = toNumber(point[key])
    if (num !== null) return num
  }
  return null
}

function resolveReportDeviceData(
  reportData: Record<string, GenerateReportDeviceData> | undefined,
  deviceSN: string
): GenerateReportDeviceData | null {
  if (!reportData) return null
  if (reportData[deviceSN]) return reportData[deviceSN]

  const baseSN = String(deviceSN || '').split('#')[0]
  if (reportData[baseSN]) return reportData[baseSN]

  for (const device of Object.values(reportData)) {
    if (!device) continue
    if (device.deviceSN === deviceSN) return device
    if (device.baseSN === baseSN) return device
  }
  return null
}

export type TemperatureChartVariant = 'blue' | 'gold'

export interface TemperatureChartBaseProps {
  variant: TemperatureChartVariant
  deviceSN?: string | null
  credentials?: { apiKey?: string; accessToken?: string; appId?: string }
}

const VARIANT_STYLES: Record<
  TemperatureChartVariant,
  {
    stroke: string
    gradientId: string
    gradientFrom: string
    gradientTo: string
    dropdownActiveClass: string
  }
> = {
  blue: {
    stroke: '#38bdf8',
    gradientId: 'tempGradient-blue',
    gradientFrom: '#38bdf8',
    gradientTo: '#38bdf8',
    dropdownActiveClass: 'temperature-chart__dropdown--blue',
  },
  gold: {
    stroke: '#fbbf24',
    gradientId: 'tempGradient-gold',
    gradientFrom: '#fbbf24',
    gradientTo: '#fbbf24',
    dropdownActiveClass: 'temperature-chart__dropdown--gold',
  },
}

export function TemperatureChartBase({ variant, deviceSN, credentials }: TemperatureChartBaseProps) {
  const hasDeviceSN = Boolean(deviceSN?.trim())
  const hasAuthCred = Boolean(credentials?.apiKey || credentials?.accessToken)
  const shouldQuerySensors = Boolean(hasDeviceSN && hasAuthCred)
  const [range, setRange] = useState<TimeRange>('24H')
  const [sensorOptions, setSensorOptions] = useState<ChartableSensorOption[]>(
    shouldQuerySensors ? [] : TEMPERATURE_SENSOR_OPTIONS
  )
  const [sensor, setSensor] = useState<ChartableSensorId>('temperature')
  const [loading, setLoading] = useState(shouldQuerySensors)
  const [dataLoading, setDataLoading] = useState(shouldQuerySensors)
  const [data, setData] = useState<Array<{ time: number; value: number }>>(() =>
    generateMockData('24H', 'temperature')
  )

  useEffect(() => {
    let cancelled = false
    if (!hasDeviceSN) {
      setSensorOptions(TEMPERATURE_SENSOR_OPTIONS)
      setLoading(false)
      return () => {
        cancelled = true
      }
    }
    if (!hasAuthCred) {
      setSensorOptions([])
      setLoading(false)
      return () => {
        cancelled = true
      }
    }

    setLoading(true)
    getChartableSensors(deviceSN, credentials)
      .then((opts) => {
        if (!cancelled) {
          const filteredOptions = filterTemperatureSensors(opts)
          setSensorOptions(filteredOptions)
          setSensor((prev) => {
            const has = filteredOptions.some((o) => o.id === prev)
            return has ? prev : (filteredOptions[0]?.id ?? 'temperature')
          })
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [deviceSN, credentials?.accessToken, credentials?.apiKey, credentials?.appId, hasDeviceSN, hasAuthCred])
  useEffect(() => {
    let cancelled = false

    if (!hasDeviceSN) {
      setData(generateMockData(range, sensor))
      setDataLoading(false)
      return () => {
        cancelled = true
      }
    }
    if (!hasAuthCred) {
      setData([])
      setDataLoading(false)
      return () => {
        cancelled = true
      }
    }

    setDataLoading(true)
    const reportScope = mapTimeRangeToGenerateReport(range)
    querySensorReportWithAuthContext(
      {
        apiKey: credentials?.apiKey,
        accessToken: credentials?.accessToken,
        appId: credentials?.appId,
      },
      deviceSN.trim(),
      {
        duration: reportScope.duration,
        granularity: reportScope.granularity,
        metrics: 'auto',
        format: 'summary',
      }
    )
      .then((reportResponse) => {
        if (cancelled) return
        const deviceData = resolveReportDeviceData(reportResponse.data, deviceSN.trim())
        const rows = Array.isArray(deviceData?.chartData) ? deviceData.chartData : []
        const next = rows
          .map((row) => {
            const point = row as Record<string, unknown>
            const time = toTimestamp(point.time)
            const value = resolveSensorValue(point, sensor)
            if (time === null || value === null) return null
            return { time, value }
          })
          .filter((point): point is { time: number; value: number } => point !== null)
          .sort((a, b) => a.time - b.time)
        setData(next)
      })
      .catch(() => {
        if (cancelled) return
        setData([])
      })
      .finally(() => {
        if (cancelled) return
        setDataLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [
    deviceSN,
    range,
    sensor,
    credentials?.apiKey,
    credentials?.accessToken,
    credentials?.appId,
    hasDeviceSN,
    hasAuthCred,
  ])
  const styles = VARIANT_STYLES[variant]
  const cfg = CHARTABLE_SENSOR_CONFIG[sensor]

  const stats = useMemo(() => {
    if (data.length === 0) return { current: null, max: null, min: null }
    const vals = data.map((d) => d.value)
    const current = vals[vals.length - 1] ?? 0
    const max = Math.max(...vals)
    const min = Math.min(...vals)
    return { current, max, min }
  }, [data])

  const formatStatValue = (value: number | null): string => {
    if (value == null) return '--'
    return `${value}`
  }

  const formatY = (v: number) => (cfg.unit === '%' ? `${v}` : cfg.unit === '°C' ? `${v}°` : `${v}`)
  const formatTooltip = (value: number | string): [string, string] => {
    const numeric = typeof value === 'number' ? value : Number(value || 0)
    return cfg ? [`${numeric}${cfg.unit}`, cfg.label] : ['', '']
  }

  if (loading || dataLoading) {
    return (
      <div className={`temperature-chart temperature-chart--${variant}`}>
        <div className="temperature-chart__loading">Loading sensors...</div>
      </div>
    )
  }

  if (sensorOptions.length === 0) {
    return (
      <div className={`temperature-chart temperature-chart--${variant}`}>
        <div className="temperature-chart__loading">No sensor data</div>
      </div>
    )
  }

  return (
    <div className={`temperature-chart temperature-chart--${variant}`}>
      <div className="temperature-chart__controls">
        <div className={`temperature-chart__dropdown ${styles.dropdownActiveClass}`}>
          <span className="temperature-chart__dropdown-icon material-symbols-rounded" aria-hidden>schedule</span>
          <select
            id={`tc-range-${variant}`}
            value={range}
            onChange={(e) => setRange(e.target.value as TimeRange)}
            className="temperature-chart__select"
            aria-label="Time range"
          >
            {TIME_RANGES.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </select>
        </div>
        <div className={`temperature-chart__dropdown ${styles.dropdownActiveClass}`}>
          <span className="temperature-chart__dropdown-icon material-symbols-rounded" aria-hidden>thermostat</span>
          <select
            id={`tc-sensor-${variant}`}
            value={sensor}
            onChange={(e) => setSensor(e.target.value as ChartableSensorId)}
            className="temperature-chart__select"
            aria-label="Sensor type"
          >
            {sensorOptions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div
        className="temperature-chart__chart"
        data-css-slot="chartLine"
        data-css-slot-label="Chart Line"
      >
        {data.length === 0 ? (
          <div className="temperature-chart__loading">No sensor data</div>
        ) : (
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
              <defs>
                <linearGradient id={styles.gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={styles.gradientFrom} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={styles.gradientTo} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis
                dataKey="time"
                type="number"
                domain={['dataMin', 'dataMax']}
                tick={{ fill: 'rgba(148,163,184,0.8)', fontSize: 10 }}
                axisLine={{ stroke: 'rgba(255,255,255,0.2)' }}
                tickLine={{ stroke: 'rgba(255,255,255,0.2)' }}
                tickFormatter={(value) => formatTimeLabel(Number(value), range)}
              />
              <YAxis
                domain={['dataMin - 2', 'dataMax + 2']}
                tick={{ fill: 'rgba(148,163,184,0.8)', fontSize: 10 }}
                axisLine={{ stroke: 'rgba(255,255,255,0.2)' }}
                tickLine={{ stroke: 'rgba(255,255,255,0.2)' }}
                tickFormatter={formatY}
              />
              <Tooltip
                contentStyle={{
                  background: 'rgba(15,23,42,0.95)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: 8,
                }}
                labelStyle={{ color: '#94a3b8' }}
                labelFormatter={(label) => formatTimeLabel(Number(label), range)}
                formatter={(value) => formatTooltip(value)}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke={styles.stroke}
                strokeWidth={2}
                fill={`url(#${styles.gradientId})`}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
      <div className="temperature-chart__stats">
        <span>Now {formatStatValue(stats.current)}{stats.current == null ? '' : cfg.unit}</span>
        <span>Max {formatStatValue(stats.max)}{stats.max == null ? '' : cfg.unit}</span>
        <span>Min {formatStatValue(stats.min)}{stats.min == null ? '' : cfg.unit}</span>
      </div>
    </div>
  )
}
